import { useQuery } from '@tanstack/react-query'
import { useContext } from 'react'
import type { ReactNode } from 'react'

import { MODALS_ID, QUERY_KEY } from '../../../constants'
import type { components } from '../../../constants/schema'

import { downloadAttachment, toast } from '../../../utils'

import { rootStore } from '../../../provider/StoreProvider'
import { ModalContext } from '../../../provider/ModalProvider'

import { useNavigate } from '../../../hooks/useNavigate'

import { getUserProductDetail, getUserProductSettle } from '../../../api/product'

import { ColumnItem, RowBetween } from '../../../components/common'
import { Button } from '../../../components/button'
import { Text } from '../../../components/text'
import { DivideLine } from '../../../components/divide'
import { CancelProductConfirmModal } from '../modals/CancelProductConfirmModal'
import { RowData } from './RowData'

import { ReactComponent as DownloadIcon } from '../../../assets/ico/common/download_18.svg'

interface ProductInfoProps {
  userProductId: components['schemas']['UserProductDto']['id']
  type: components['schemas']['UserProductDto']['status']
}

async function downloadProductFile(
  activeProductSale: components['schemas']['ProductSaleDto'] | undefined,
  type: components['schemas']['ProductSaleFileItemRelationDto']['productSaleFileItemType'],
) {
  const { commonStore } = rootStore

  if (typeof activeProductSale === 'undefined' || !activeProductSale.files?.length) {
    toast('문서를 불러오지 못했습니다.', undefined, false)
    return
  }

  const fileItem = activeProductSale.files.find(
    ({ productSaleFileItemType }) => productSaleFileItemType === type,
  )?.fileItem

  if (!fileItem || !fileItem.filePath) {
    toast('문서를 불러오지 못했습니다.', undefined, false)
    return
  }

  const { filePath, filename } = fileItem

  commonStore.setIsLoading(true)

  await downloadAttachment(filePath, filename)

  setTimeout(() => {
    commonStore.setIsLoading(false)
  }, 500)
}

function DownloadButton({ children, onClick }: { children: ReactNode; onClick?: () => void }) {
  return (
    <Button type="dark" size="compact" block={true} onClick={onClick}>
      <Text size={14} weight={500} color="text_white">
        {children}
      </Text>
      <DownloadIcon />
    </Button>
  )
}

export function ProductInfo({ userProductId, type }: ProductInfoProps) {
  switch (type) {
    case 'SALE': // 청약 신청
      return <SaleInfo userProductId={userProductId} />
    case 'SALE_COMPLETED': // 배정 확정
      return <AllocationInfo userProductId={userProductId} />
    case 'SELL': // 매각 중
      return <SellInfo userProductId={userProductId} />
    case 'TO_BE_SETTLEMENT': // 정산 예정
      return <SettlementInfo userProductId={userProductId} />
    case 'SETTLEMENT_FINISH': // 정산 완료
      return <CompleteSettlementInfo userProductId={userProductId} />
    case 'CANCEL_OR_REFUND': // 청약 취소 혹은 환불
    default:
      return null
  }
}

/**
 * 청약 신청
 * @param UserProductDto
 */
function SaleInfo({ userProductId }: { userProductId: components['schemas']['UserProductDto']['id'] }) {
  const { openModal, closeModal } = useContext(ModalContext)

  const {
    data: userProductDetail,
    isPending,
    isError,
  } = useQuery({
    queryKey: [QUERY_KEY.userProduct, { userProductId }],
    queryFn: async () => await getUserProductDetail(userProductId),
  })

  if (isPending || isError) return null

  const {
    balance,
    offerPrice,
    subscriptionShares,
    cancelable,
    product: { id: productId, storagelocation, activeProductSale },
  } = userProductDetail

  const navigate = useNavigate()

  const openCancelModal = () =>
    openModal({
      id: MODALS_ID.CANCEL_PRODUCT,
      Component: () => (
        <CancelProductConfirmModal
          userProductId={userProductId}
          productId={productId}
          onDismiss={() => {
            closeModal(MODALS_ID.CANCEL_PRODUCT)
            toast('작품 청약이 취소되었습니다.')
            const searchParams = new URLSearchParams(window.location.search)
            const redirectUrl = searchParams.get('redirectUrl')
            if (redirectUrl) {
              navigate(redirectUrl, { replace: true })
              return
            }
            navigate(-1)
          }}
        />
      ),
    })

  return (
    <>
      <RowData title="총 신청 금액" content={`${balance?.toLocaleString()}원`} />
      <RowData title="모집가액" content={`${offerPrice?.toLocaleString()}원`} />
      <RowData title="청약 신청수량" content={`${subscriptionShares?.toLocaleString()}주`} />
      <RowData title="작품 위치" content={storagelocation ?? '-'} />
      <DivideLine />
      {/* TODO: 파일 타입 filter */}
      <RowBetween $gap={8}>
        {activeProductSale?.files
          ?.filter(
            ({ productSaleFileItemType }) =>
              productSaleFileItemType === 'DOCS_INVESTOR_MANUAL' ||
              productSaleFileItemType === 'DOCS_INVESTMENT_CONTRACT',
          )
          .map(({ fileItemId, productSaleFileItemLabel, productSaleFileItemType }) => {
            const download = () => downloadProductFile(activeProductSale, productSaleFileItemType)
            return (
              <DownloadButton key={fileItemId} onClick={download}>
                {productSaleFileItemLabel}
              </DownloadButton>
            )
          })}
      </RowBetween>
      {/* TODO: 청약 취소 후 보유자산 리스트로 replace */}
      {cancelable && (
        <Button type="default" size="large" onClick={openCancelModal}>
          청약 취소
        </Button>
      )}
    </>
  )
}

/**
 * 배정 확정
 * @param UserProductDto
 */
function AllocationInfo({ userProductId }: { userProductId: components['schemas']['UserProductDto']['id'] }) {
  const {
    data: userProductDetail,
    isPending,
    isError,
  } = useQuery({
    queryKey: [QUERY_KEY.userProduct, { userProductId }],
    queryFn: async () => await getUserProductDetail(userProductId),
  })

  if (isPending || isError) return null

  const {
    balance,
    offerPrice,
    subscriptionShares,
    totalShares,
    equalAllocatedShares,
    proRataAllocatedShares,
    unallocatedShares,
    product: { storagelocation, activeProductSale },
  } = userProductDetail

  return (
    <>
      <RowData title="배정금액" content={`${balance?.toLocaleString()}원`} />
      <RowData title="모집가액" content={`${offerPrice?.toLocaleString()}원`} />
      <RowData title="청약 신청수량" content={`${subscriptionShares?.toLocaleString()}주`} />
      <RowData title="배정수량" content={`${totalShares?.toLocaleString()}주`} />
      <RowData
        isChildren={true}
        title={{ value: '균등 배정수량', size: 16 }}
        content={{ value: `${equalAllocatedShares?.toLocaleString()}주`, size: 16 }}
      />
      <RowData
        isChildren={true}
        title={{ value: '비례 배정수량', size: 16 }}
        content={{ value: `${proRataAllocatedShares?.toLocaleString()}주`, size: 16 }}
      />
      <RowData title="환불수량" content={`${unallocatedShares?.toLocaleString()}주`} />
      <RowData title="작품 위치" content={storagelocation ?? '-'} />
      <DivideLine />
      <ColumnItem $gap={8}>
        <RowBetween $gap={8}>
          {activeProductSale?.files
            ?.filter(
              ({ productSaleFileItemType }) =>
                productSaleFileItemType === 'DOCS_INVESTOR_MANUAL' ||
                productSaleFileItemType === 'DOCS_INVESTMENT_CONTRACT',
            )
            .map(({ fileItemId, productSaleFileItemLabel, productSaleFileItemType }) => {
              const download = () => downloadProductFile(activeProductSale, productSaleFileItemType)
              return (
                <DownloadButton key={fileItemId} onClick={download}>
                  {productSaleFileItemLabel}
                </DownloadButton>
              )
            })}
        </RowBetween>
        <DownloadButton onClick={() => downloadProductFile(activeProductSale, 'DOCS_INVESTOR_LIST')}>
          투자자 명부
        </DownloadButton>
      </ColumnItem>
    </>
  )
}

/**
 * 매각 중
 * @param UserProductDto
 */
function SellInfo({ userProductId }: { userProductId: components['schemas']['UserProductDto']['id'] }) {
  const {
    data: userProductDetail,
    isPending,
    isError,
  } = useQuery({
    queryKey: [QUERY_KEY.userProduct, { userProductId }],
    queryFn: async () => await getUserProductDetail(userProductId),
  })

  if (isPending || isError) return null

  const {
    id,
    offerPrice,
    subscriptionShares,
    totalShares,
    equalAllocatedShares,
    proRataAllocatedShares,
    unallocatedShares,
    product: { storagelocation, activeProductSale },
  } = userProductDetail

  return (
    <>
      <RowData title="배정금액" content={`${(offerPrice * totalShares)?.toLocaleString()}원`} />
      <RowData title="모집가액" content={`${offerPrice?.toLocaleString()}원`} />
      <RowData title="청약 신청수량" content={`${subscriptionShares?.toLocaleString()}주`} />
      <RowBetween>
        <Text size={16} color="text_secondary">
          배정수량
        </Text>
        <Text size={16} weight={500}>
          {totalShares?.toLocaleString()}주
        </Text>
      </RowBetween>
      <RowData
        isChildren={true}
        title={{ value: '균등 배정수량', size: 16 }}
        content={{ value: `${equalAllocatedShares?.toLocaleString()}주`, size: 16 }}
      />
      <RowData
        isChildren={true}
        title={{ value: '비례 배정수량', size: 16 }}
        content={{ value: `${proRataAllocatedShares?.toLocaleString()}주`, size: 16 }}
      />
      <RowData title="환불수량" content={`${unallocatedShares?.toLocaleString()}주`} />
      <RowData title="작품 위치" content={storagelocation ?? ''} />
      <DivideLine />
      <ColumnItem $gap={8}>
        <RowBetween $gap={8}>
          {activeProductSale?.files
            ?.filter(
              ({ productSaleFileItemType }) =>
                productSaleFileItemType === 'DOCS_INVESTOR_MANUAL' ||
                productSaleFileItemType === 'DOCS_INVESTMENT_CONTRACT',
            )
            .map(({ fileItemId, productSaleFileItemLabel, productSaleFileItemType }) => {
              const download = () => downloadProductFile(activeProductSale, productSaleFileItemType)
              return (
                <DownloadButton key={fileItemId} onClick={download}>
                  {productSaleFileItemLabel}
                </DownloadButton>
              )
            })}
        </RowBetween>
        <DownloadButton onClick={() => downloadProductFile(activeProductSale, 'DOCS_INVESTOR_LIST')}>
          투자자 명부
        </DownloadButton>
      </ColumnItem>
      <Button type="primary" size="large" href={`/myPage/asset/${id}/saleInfo`}>
        매각 정보 확인하기
      </Button>
    </>
  )
}

/**
 * 정산 예정
 * @param UserProductDto
 */
function SettlementInfo({ userProductId }: { userProductId: components['schemas']['UserProductDto']['id'] }) {
  const {
    data: userProductDetail,
    isPending,
    isError,
  } = useQuery({
    queryKey: [QUERY_KEY.userProductSettle, { userProductId }],
    queryFn: async () => await getUserProductSettle(userProductId),
    enabled: typeof userProductId === 'number',
  })

  if (isPending || isError) return null

  return (
    <>
      <RowData title="배정수량" content={`${(userProductDetail.totalShares ?? 0).toLocaleString()}주`} />
      <RowData title="세후청산금" content={`${(userProductDetail.balance ?? 0).toLocaleString()}원`} />
      <Button
        type="primary"
        size="large"
        href={`/myPage/asset/${userProductId}/calculate`}
        style={{ marginTop: '8px' }}
      >
        정산하기
      </Button>
    </>
  )
}

/**
 * 정산 완료
 * @param UserProductDto
 */
function CompleteSettlementInfo({ userProductId }: { userProductId: components['schemas']['UserProductDto']['id'] }) {
  const {
    data: userProductDetail,
    isPending,
    isError,
  } = useQuery({
    queryKey: [QUERY_KEY.userProductSettle, { userProductId }],
    queryFn: async () => await getUserProductSettle(userProductId),
    enabled: typeof userProductId === 'number',
  })

  if (isPending || isError) return null

  return (
    <>
      <RowData title="배정수량" content={`${(userProductDetail.totalShares ?? 0).toLocaleString()}주`} />
      <RowData title="세후청산금" content={`${(userProductDetail.balance ?? 0).toLocaleString()}원`} />
      <Button type="default" size="large" href={`/myPage/asset/${userProductId}/settlement`}>
        정산 내역 확인하기
      </Button>
    </>
  )
}
